import { RDO_SITUATION_OPTIONS } from '@/modules/field/types/rdo-form';
import type { RdoAtividade } from '@/modules/field/types/rdo-form';
import {
  hydrateApontarFromApontamentos,
  normalizeLoadedAtividade,
} from '@/modules/field/utils/rdoApontamentos';
import { calculateParentCompletion } from '@/modules/field/utils/scheduleParentCompletion';

export type PhaseKey = 'cabo' | 'instal' | 'confg';

export type ActivityBar = {
  label: string;
  phase: PhaseKey;
  current: number;
  total: number;
  pct: number;
  complete: boolean;
  unit: string;
  /** Quantidade apontada neste RDO */
  apontado: number;
};

const TASK_TYPE_TO_PHASE: Record<string, PhaseKey> = {
  cabling: 'cabo',
  installation: 'instal',
  configuration: 'confg',
};

const APONTAMENTO_LABEL: Record<string, string> = {
  cabling: 'CABO',
  installation: 'INSTAL',
  configuration: 'CONFG',
};

const PHASE_UNIT: Record<PhaseKey, string> = {
  cabo: 'un',
  instal: 'un',
  confg: 'un',
};

export const APONTAR_PHASES: PhaseKey[] = ['cabo', 'instal', 'confg'];

const PHASE_LABEL: Record<PhaseKey, string> = {
  cabo: 'CABO',
  instal: 'INSTAL',
  confg: 'CONFG',
};

function phaseExistsInStructure(act: RdoAtividade, phase: PhaseKey): boolean {
  return (act.phases ?? []).some(
    (row) => TASK_TYPE_TO_PHASE[row.taskType] === phase,
  );
}

/** Garante progress/phases/apontar para exibição (payload só com apontamentos). */
export function enrichAtividadeForDisplay(raw: RdoAtividade): RdoAtividade {
  const act = normalizeLoadedAtividade(raw);
  const apontamentos = act.apontamentos ?? [];

  if ((!act.progress?.length || act.progress.every((p) => !p.total)) && apontamentos.length) {
    act.progress = apontamentos.map((row) => ({
      label: APONTAMENTO_LABEL[row.taskType] ?? row.label ?? '—',
      current: Number(row.actualQuantity) || 0,
      total: Number(row.totalQuantity) || 0,
    }));
  }

  if (!act.phases?.length && apontamentos.length) {
    act.phases = apontamentos.map((row) => ({
      scheduleItemId: row.scheduleItemId,
      taskType: row.taskType,
      label: APONTAMENTO_LABEL[row.taskType] ?? row.label,
      current: Number(row.actualQuantity) || 0,
      total: Number(row.totalQuantity) || 0,
      estimatedTimeHours: Number(row.estimatedTimeHours) || 0,
    }));
  }

  return act;
}

export function activityTags(act: RdoAtividade): string[] {
  const deviceUpper = (act.device ?? '').toUpperCase();
  return act.tags
    .slice(1)
    .filter((t) => t.toUpperCase() !== deviceUpper);
}

export function activityTitle(act: RdoAtividade): string {
  return act.title || act.device || 'Atividade';
}

function isApontedPhase(act: RdoAtividade, phase: PhaseKey): boolean {
  return (act.apontar[phase] ?? 0) > 0;
}

export function activityBars(act: RdoAtividade): ActivityBar[] {
  const normalized = enrichAtividadeForDisplay(act);

  const bars = (normalized.phases ?? [])
    .map((phase) => {
      const phaseKey = TASK_TYPE_TO_PHASE[phase.taskType];
      if (!phaseKey) {
        return null;
      }

      const apontado = normalized.apontar[phaseKey] || 0;
      const total = phase.total || 1;
      const current = phase.current + apontado;
      const pct = Math.min(
        100,
        Math.max(0, Math.round((current / total) * 100)),
      );

      return {
        label: phase.label,
        phase: phaseKey,
        current,
        total: phase.total,
        pct,
        complete: pct >= 100,
        unit: PHASE_UNIT[phaseKey],
        apontado,
      };
    })
    .filter((b): b is ActivityBar => b !== null);

  return bars.filter(
    (bar) => bar.apontado > 0 || isApontedPhase(normalized, bar.phase),
  );
}

function phaseLiveCompletionPct(
  phase: RdoAtividade['phases'][number],
  apontado: number,
): number {
  const total = Number(phase.total) || 0;

  if (total > 0) {
    const current = (Number(phase.current) || 0) + apontado;
    return Math.min(100, Math.max(0, (current / total) * 100));
  }

  return 0;
}

export function hasActivityApontamento(act: RdoAtividade): boolean {
  const normalized = enrichAtividadeForDisplay(act);
  const apontarTotal =
    (normalized.apontar.cabo ?? 0) +
    (normalized.apontar.instal ?? 0) +
    (normalized.apontar.confg ?? 0);

  if (apontarTotal > 0) {
    return true;
  }

  if (
    normalized.isPendingType &&
    (Number(normalized.completionPercentage) || 0) > 0
  ) {
    return true;
  }

  return (normalized.apontamentos ?? []).some(
    (row) => (Number(row.quantity) || 0) > 0,
  );
}

export function syncActivitySituationFromApontamento(act: RdoAtividade): void {
  if (!hasActivityApontamento(act)) {
    return;
  }

  if (act.situation === 'completed' || act.situation === 'blocked') {
    return;
  }

  act.situation = 'in_progress';
  act.status =
    RDO_SITUATION_OPTIONS.find((o) => o.value === 'in_progress')?.label ??
    'Em andamento';
}

export function activityOverallPct(act: RdoAtividade): number {
  if (act.isPendingType && act.completionPercentage != null) {
    return Math.round(Number(act.completionPercentage));
  }

  const normalized = enrichAtividadeForDisplay(act);
  const phases = normalized.phases ?? [];

  if (!phases.length) {
    return 0;
  }

  const children = phases.map((phase) => {
    const phaseKey = TASK_TYPE_TO_PHASE[phase.taskType];
    const apontado = phaseKey ? normalized.apontar[phaseKey] || 0 : 0;

    return {
      completion_percentage: phaseLiveCompletionPct(phase, apontado),
      estimated_time_hours: Number(phase.estimatedTimeHours) || 0,
      total_value: 0,
    };
  });

  return Math.round(calculateParentCompletion(children));
}

export function activityStatusLabel(act: RdoAtividade): string {
  if (activityOverallPct(act) >= 100) {
    return (
      RDO_SITUATION_OPTIONS.find((o) => o.value === 'completed')?.label ??
      'Concluída'
    );
  }

  if (hasActivityApontamento(act)) {
    return (
      RDO_SITUATION_OPTIONS.find((o) => o.value === 'in_progress')?.label ??
      'Em andamento'
    );
  }

  return act.status || '—';
}

export function activityBadgeClass(act: RdoAtividade): string {
  if (activityOverallPct(act) >= 100) {
    return 'bg-emerald-100 text-emerald-800';
  }

  if (hasActivityApontamento(act)) {
    return 'bg-sky-100 text-sky-800';
  }

  return 'bg-amber-100 text-amber-800';
}

export function hasActivityPhase(act: RdoAtividade, phase: PhaseKey): boolean {
  const normalized = enrichAtividadeForDisplay(act);

  return (
    (normalized.apontar[phase] ?? 0) > 0 || phaseExistsInStructure(normalized, phase)
  );
}

/** Fases com quantidade já apontada ou coluna do cronograma disponível. */
export function visibleApontarPhases(act: RdoAtividade): PhaseKey[] {
  const normalized = enrichAtividadeForDisplay(act);

  return APONTAR_PHASES.filter(
    (phase) =>
      (normalized.apontar[phase] ?? 0) > 0 ||
      phaseExistsInStructure(normalized, phase),
  );
}

/** Saldo disponível para apontar (previsto − realizado) na atividade já incluída no RDO. */
export function maxApontarForAtividade(
  act: RdoAtividade,
  phase: PhaseKey,
): number {
  const normalized = enrichAtividadeForDisplay(act);

  const phaseRow = (normalized.phases ?? []).find(
    (row) => TASK_TYPE_TO_PHASE[row.taskType] === phase,
  );
  if (!phaseRow) {
    return 0;
  }

  return Math.max(
    0,
    (Number(phaseRow.total) || 0) - (Number(phaseRow.current) || 0),
  );
}

export function parseAtividadeApontarInputValue(value: unknown): number {
  return Math.max(0, Number.isFinite(Number(value)) ? Number(value) : 0);
}

export function isAtividadeApontarWithinBalance(
  act: RdoAtividade,
  phase: PhaseKey,
  value: number,
): boolean {
  return value <= maxApontarForAtividade(act, phase);
}

/** Retorna a quantidade normalizada ou `null` se exceder o saldo (não aplica clamp). */
export function tryApplyAtividadeApontar(
  act: RdoAtividade,
  phase: PhaseKey,
  rawValue: unknown,
): number | null {
  const max = maxApontarForAtividade(act, phase);
  if (max <= 0) {
    return 0;
  }

  const value = parseAtividadeApontarInputValue(rawValue);
  if (value > max) {
    return null;
  }

  return value;
}

export function clampAtividadeApontar(
  act: RdoAtividade,
  phase: PhaseKey,
  value: number,
): number {
  const max = maxApontarForAtividade(act, phase);
  const normalized = parseAtividadeApontarInputValue(value);

  return Math.min(normalized, max);
}

export function estimateReportProgressFromPayload(
  payload: Record<string, unknown>,
): number {
  const raw = payload.atividades;
  if (!Array.isArray(raw) || raw.length === 0) {
    return 0;
  }

  const pcts = raw.map((row) =>
    activityOverallPct(enrichAtividadeForDisplay(row as RdoAtividade)),
  );

  const sum = pcts.reduce((acc, value) => acc + value, 0);
  return Math.min(100, Math.round(sum / pcts.length));
}

export function apontamentosSummary(act: RdoAtividade): string {
  const bars = activityBars(act);
  if (!bars.length) {
    if (act.isPendingType && act.completionPercentage != null) {
      return `${Math.round(Number(act.completionPercentage))}% conclusão`;
    }

    return 'Sem apontamento';
  }

  return bars
    .filter((bar) => bar.apontado > 0)
    .map(
      (bar) =>
        `${bar.label}: +${bar.apontado}${bar.unit} (${bar.current}/${bar.total})`,
    )
    .join(' · ');
}
